/**
 * ShippingProtection Web Component
 * Manages shipping protection toggle in cart
 * @extends HTMLElement
 */
class ShippingProtection extends HTMLElement {
  constructor() {
    super();

    this.shippingProtectionProductId = this.dataset.shippingProtectionId;
    this.isShippingProtectionEnabled = this.dataset.shippingProtectionEnabled;
    this.shippingProtectionInCart = parseInt(this.getAttribute('shipping-protection-in-cart')) || 0;
    this.sectionId = this.dataset.sectionId;
    this.isUpdating = false;
    
    if (this.shippingProtectionProductId) {
      window.FoxTheme = window.FoxTheme || {};
      window.FoxTheme.shippingProtectionVariantId = parseInt(this.shippingProtectionProductId);
    }
  }

  connectedCallback() {
    this.initializeShippingProtectionCheckbox();
    this.handleInitialShippingProtectionState();
    
    // Listen for cart updates to re-initialize if DOM was replaced
    this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      this.handleCartUpdate.bind(this)
    );
  }
  
  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }
  
  handleCartUpdate(event) {
    // Re-initialize after cart update in case DOM was replaced
    requestAnimationFrame(() => {
      const checkbox = this.querySelector('[name="attributes[shipping-protection]"]');
      if (checkbox && !checkbox.hasAttribute('data-initialized')) {
        checkbox.setAttribute('data-initialized', 'true');
        this.initializeShippingProtectionCheckbox();
        this.handleInitialShippingProtectionState();
      }
    });
  }

  initializeShippingProtectionCheckbox() {
    const checkbox = this.querySelector('[name="attributes[shipping-protection]"]');
    if (!checkbox) return;
    
    // Remove existing listener to prevent duplicates
    if (checkbox.hasAttribute('data-listener-attached')) {
      return;
    }
    checkbox.setAttribute('data-listener-attached', 'true');

    checkbox.addEventListener('change', (event) => {
      if (this.isUpdating) {
        event.preventDefault();
        return;
      }

      const isChecked = event.target.checked;
      this.handleToggleChange(isChecked);
    });
  }

  handleInitialShippingProtectionState() {
    const checkbox = this.querySelector('[name="attributes[shipping-protection]"]');
    if (!checkbox) return;

    // ⭐ ON BY DEFAULT: Sync checkbox with cart state
    const shouldBeChecked = this.shippingProtectionInCart > 0;
    checkbox.checked = shouldBeChecked;

    // If NOT in cart yet, add it automatically (ON by default)
    if (this.shippingProtectionInCart === 0) {
      this.addShippingProtectionSilently();
    }
    
    // Ensure cart attribute matches the product state
    if (this.shippingProtectionInCart > 0 && this.isShippingProtectionEnabled !== 'yes') {
      // Product is in cart but attribute is missing - add it silently
      this.updateCartAttribute('yes');
    }
  }
  
  /**
   * Add shipping protection without showing loader (silent)
   */
  addShippingProtectionSilently() {
    const requestBody = this.createRequestBody(
      { [this.shippingProtectionProductId]: 1 },
      { 'shipping-protection': 'yes' }
    );
    
    fetch(`${FoxTheme.routes.cart_update_url}`, {
      ...FoxTheme.utils.fetchConfig(),
      body: requestBody
    }).then(() => {
      // Update local state after adding
      this.shippingProtectionInCart = 1;
    });
  }

  /**
   * Handle toggle change
   * @param {boolean} isChecked - Current checkbox state
   */
  async handleToggleChange(isChecked) {
    this.isUpdating = true;
    this.showLoader();

    try {
      if (isChecked) {
        await this.addShippingProtection();
      } else {
        await this.removeShippingProtection();
      }
    } catch (error) {
      console.error('Error updating shipping protection:', error);
      this.revertCheckboxState();
    } finally {
      this.isUpdating = false;
      this.hideLoader();
    }
  }

  /**
   * Update cart attribute without triggering cart update event
   * @param {string} value - Attribute value
   */
  updateCartAttribute(value) {
    const requestBody = JSON.stringify({
      attributes: { 'shipping-protection': value }
    });
    
    return fetch(`${FoxTheme.routes.cart_update_url}`, {
      ...FoxTheme.utils.fetchConfig(),
      body: requestBody
    });
  }

  async addShippingProtection() {
    const requestBody = this.createRequestBody(
      { [this.shippingProtectionProductId]: 1 },
      { 'shipping-protection': 'yes' }
    );
    
    await this.updateCart(requestBody);
  }

  async removeShippingProtection() {
    const requestBody = this.createRequestBody(
      { [this.shippingProtectionProductId]: 0 },
      { 'shipping-protection': '' }
    );
    
    await this.updateCart(requestBody);
  }

  getSectionsToUpdate() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  }

  createRequestBody(updates, attributes) {
    const sections = this.getSectionsToUpdate();
    return JSON.stringify({ updates, attributes, sections });
  }

  /**
   * Update cart and handle response
   * @param {string} body - Request body
   * @returns {Promise}
   */
  async updateCart(body) {
    try {
      const response = await fetch(`${FoxTheme.routes.cart_update_url}`, {
        ...FoxTheme.utils.fetchConfig(),
        body
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const parsedState = await response.json();
      
      // Publish cart update event
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { 
        cart: parsedState,
        preserveShippingProtectionState: true 
      });

      return parsedState;
    } catch (error) {
      console.error('Error updating cart:', error);
      throw error;
    }
  }

  revertCheckboxState() {
    const checkbox = this.querySelector('[name="attributes[shipping-protection]"]');
    if (checkbox) {
      checkbox.checked = !checkbox.checked;
    }
  }

  hideLoader() {
    const loaderElement = this.querySelector('.loader');
    if (loaderElement) {
      loaderElement.classList.remove('btn--loading');
      // Reset toggle styles
      const toggle = this.querySelector('.shipping-protection__toggle');
      if (toggle) {
        toggle.style.opacity = '';
        toggle.style.visibility = '';
      }
    }
  }

  showLoader() {
    const loaderElement = this.querySelector('.loader');
    if (loaderElement) {
      loaderElement.classList.add('btn--loading');
      // Ensure toggle remains visible even when loader is active
      const toggle = this.querySelector('.shipping-protection__toggle');
      if (toggle) {
        toggle.style.opacity = '1';
        toggle.style.visibility = 'visible';
      }
    }
  }
}

customElements.define('shipping-protection', ShippingProtection);